/**
 * These types are no longer public. No official solution to replace them,
 * so the most common suggestion is declaring them locally.
 */

import { FormControl, FormGroupDirective, NgControl, NgForm } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { Subject } from 'rxjs';

/** Helper types for mixins */
export type _Constructor<T = object> = new (...args: any[]) => T;
export type _AbstractConstructor<T = object> = abstract new (...args: any[]) => T;

/** Interfaces no longer exported by Angular */
export interface CanDisable {
  disabled: boolean;
}

export interface HasTabIndex {
  tabIndex: number;
}

export interface CanUpdateErrorState {
  errorState: boolean;
  stateChanges: Subject<void>;
  updateErrorState(): void;
}

/** Dependencies a control must expose for {@link mixinErrorState} to evaluate its error state. */
interface HasErrorStateDeps {
  ngControl: NgControl | null;
  errorStateMatcher?: ErrorStateMatcher;
  _defaultErrorStateMatcher: ErrorStateMatcher;
  _parentForm?: NgForm | null;
  _parentFormGroup?: FormGroupDirective | null;
}

/** Mixin for CanDisable */
export function mixinDisabled<T extends _Constructor>(base: T): _Constructor<CanDisable> & T {
  return class extends base {
    public disabled = false;
  };
}

/** Mixin for HasTabIndex */
export function mixinTabIndex<T extends _Constructor>(base: T): _Constructor<HasTabIndex> & T {
  return class extends base {
    public tabIndex = 0;
  };
}

/** Mixin for CanUpdateErrorState */
export function mixinErrorState<T extends _Constructor>(base: T): _Constructor<CanUpdateErrorState> & T {
  return class extends base implements CanUpdateErrorState {
    public errorState = false;
    public stateChanges = new Subject<void>();

    public updateErrorState(): void {
      const deps = this as unknown as HasErrorStateDeps;
      const oldState = this.errorState;
      const parent = deps._parentFormGroup || deps._parentForm;
      const matcher = deps.errorStateMatcher || deps._defaultErrorStateMatcher;
      const control = deps.ngControl ? (deps.ngControl.control as FormControl) : null;
      const newState = matcher ? matcher.isErrorState(control, parent) : false;

      if (newState !== oldState) {
        this.errorState = newState;
        this.stateChanges.next();
      }
    }
  };
}

/** Mixin for color property */
export function mixinColor<T extends _Constructor>(base: T, defaultColor: string = ''): _Constructor<{ color: string }> & T {
  return class extends base {
    public color = defaultColor;
  };
}
